/**
 * DashboardShell Component
 * 
 * Main application layout with:
 * - Responsive sidebar navigation
 * - Collapsible mobile menu
 * - Top bar with balance and notifications
 * - Breadcrumb navigation
 */

import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  CreditCard,
  Users,
  ShieldAlert,
  BookOpen,
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Home,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { TopBar } from './TopBar';
import { BreadcrumbNav } from './BreadcrumbNav';
import type { Customer, Notification, NavItem } from '../../types';

export interface DashboardShellProps {
  /** Page content */
  children: React.ReactNode;
  /** Current customer data */
  customer: Customer;
  /** Navigation items configuration */
  navItems?: NavItem[];
  /** Notifications data */
  notifications?: Notification[];
  /** Unread notification count */
  unreadCount?: number;
  /** Ledger balance in cents (for real-time updates) */
  ledgerBalanceCents?: number;
  /** WebSocket connection status */
  connectionStatus?: 'connected' | 'disconnected' | 'connecting';
  /** Current page title */
  pageTitle?: string;
  /** Custom breadcrumb items */
  breadcrumbs?: { label: string; path?: string }[];
  /** Callback for new payment button */
  onNewPayment?: () => void;
  /** Callback for notification click */
  onNotificationClick?: (notification: Notification) => void;
  /** Callback for logout */
  onLogout?: () => void;
  /** Optional additional CSS classes */
  className?: string;
  /** Sidebar initially collapsed (desktop) */
  defaultCollapsed?: boolean;
}

/**
 * Default navigation items
 */
const defaultNavItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: 'home',
  },
  {
    id: 'payments',
    label: 'Payments',
    path: '/payments',
    icon: 'creditCard',
  },
  {
    id: 'vendors',
    label: 'Vendors',
    path: '/vendors',
    icon: 'users',
  },
  {
    id: 'fraud-review',
    label: 'Fraud Review',
    path: '/payments/held',
    icon: 'shieldAlert',
    badge: 0,
  },
  {
    id: 'ledger',
    label: 'Ledger',
    path: '/ledger',
    icon: 'bookOpen',
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: 'settings',
  },
];

/**
 * Icon mapping
 */
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  home: Home,
  creditCard: CreditCard,
  users: Users,
  shieldAlert: ShieldAlert,
  bookOpen: BookOpen,
  settings: Settings,
};

/**
 * DashboardShell - Main application layout component
 * 
 * @example
 * <DashboardShell 
 *   customer={customer}
 *   pageTitle="Payments"
 * >
 *   <PaymentsList />
 * </DashboardShell>
 */
export const DashboardShell: React.FC<DashboardShellProps> = ({
  children,
  customer,
  navItems = defaultNavItems,
  notifications = [],
  unreadCount = 0,
  ledgerBalanceCents,
  connectionStatus = 'connected',
  pageTitle,
  breadcrumbs,
  onNewPayment,
  onNotificationClick,
  onLogout,
  className,
  defaultCollapsed = false,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(defaultCollapsed);
  const location = useLocation();

  // Update fraud review badge with actual held count
  const processedNavItems = navItems.map(item => {
    if (item.id === 'fraud-review') {
      return { ...item, badge: unreadCount };
    }
    return item;
  });

  return (
    <div className={cn('min-h-screen bg-gray-50 flex', className)}>
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ease-in-out',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarCollapsed ? 'lg:w-20' : 'w-64'
        )}
      >
        {/* Logo section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div className={cn('flex items-center', sidebarCollapsed && 'lg:justify-center w-full')}>
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className={cn(
              'ml-3 font-bold text-gray-900 transition-opacity duration-200',
              sidebarCollapsed ? 'lg:hidden' : 'block'
            )}>
              PayGuard
            </span>
          </div>
          
          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Desktop collapse button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {processedNavItems.map((item) => {
            const Icon = iconMap[item.icon];
            const isActive = location.pathname === item.path || 
                           location.pathname.startsWith(`${item.path}/`);

            return (
              <NavLink
                key={item.id}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative group',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <Icon className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                )} />
                
                <span className={cn(
                  'ml-3 transition-opacity duration-200',
                  sidebarCollapsed ? 'lg:hidden' : 'block'
                )}>
                  {item.label}
                </span>

                {/* Badge */}
                {item.badge !== undefined && item.badge > 0 && (
                  <span className={cn(
                    'bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full',
                    sidebarCollapsed ? 'lg:ml-0' : 'ml-auto'
                  )}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}

                {/* Tooltip for collapsed sidebar */}
                {sidebarCollapsed && (
                  <div className="hidden lg:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {item.label}
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-1.5 bg-red-500 px-1.5 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom section - Customer info */}
        <div className={cn(
          'absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200',
          sidebarCollapsed && 'lg:hidden'
        )}>
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
              {customer.businessName.charAt(0).toUpperCase()}
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {customer.businessName}
              </p>
              <p className="text-xs text-gray-500">
                {customer.onboardingStatus === 'approved' ? (
                  <span className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />
                    Verified
                  </span>
                ) : (
                  <span className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1.5" />
                    Pending
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <TopBar
          customer={customer}
          ledgerBalanceCents={ledgerBalanceCents}
          unreadCount={unreadCount}
          notifications={notifications}
          connectionStatus={connectionStatus}
          onNewPayment={onNewPayment}
          onNotificationClick={onNotificationClick}
          onLogout={onLogout}
        />

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {/* Page header with breadcrumbs */}
          {(pageTitle || breadcrumbs) && (
            <div className="px-4 lg:px-6 py-4 bg-white border-b border-gray-200">
              <BreadcrumbNav items={breadcrumbs} />
              {pageTitle && (
                <h1 className="text-2xl font-bold text-gray-900 mt-2">
                  {pageTitle}
                </h1>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-4 lg:p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardShell;
